import { NavLink } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard,
  Users,
  AlertTriangle,
  Network,
  UserCircle,
  Settings,
  LogOut,
  LifeBuoy,
  Waves,
  X,
  Plus,
  Power,
  AlertCircle,
  Store,
  CalendarCheck,
  Wallet,
  CheckSquare,
  Package,
  Clapperboard,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Modal } from "../components/Modal";
import { Button } from "../components/ui";

// One entry per resource the partner API actually exposes. The two menus are
// identical except for Branches: only a chain onboards outlets (§3), and
// /branches answers 403 for a branch login. Everything else — staff, the org
// tree, attendance, payroll, tasks, complaints — is scoped from the token, so
// both tiers get it and a chain simply sees more inside each page.
const CHAIN_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/branches", label: "Branches", icon: Store },
  { to: "/employees", label: "Employees", icon: Users },
  { to: "/organization", label: "Organization", icon: Network },
  { to: "/attendance", label: "Attendance", icon: CalendarCheck },
  { to: "/payroll", label: "Payroll", icon: Wallet },
  { to: "/todos", label: "Tasks", icon: CheckSquare },
  { to: "/inventory", label: "Inventory", icon: Package },
  { to: "/cinema", label: "Movie Keys", icon: Clapperboard },
  { to: "/complaints", label: "Complaints", icon: AlertTriangle },
  { to: "/profile", label: "Profile", icon: UserCircle },
  { to: "/settings", label: "Settings", icon: Settings },
];

const BRANCH_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/employees", label: "Employees", icon: Users },
  { to: "/organization", label: "Organization", icon: Network },
  { to: "/attendance", label: "Attendance", icon: CalendarCheck },
  { to: "/payroll", label: "Payroll", icon: Wallet },
  { to: "/todos", label: "Tasks", icon: CheckSquare },
  { to: "/inventory", label: "Inventory", icon: Package },
  { to: "/cinema", label: "Movie Keys", icon: Clapperboard },
  { to: "/complaints", label: "Complaints", icon: AlertTriangle },
  { to: "/profile", label: "Profile", icon: UserCircle },
  { to: "/settings", label: "Settings", icon: Settings },
];

// Skeleton loader component
const SidebarSkeleton = () => (
  <aside className="fixed inset-y-0 left-0 z-40 flex h-screen w-64 shrink-0 flex-col bg-gradient-to-b from-emerald-800 via-emerald-900 to-green-900 shadow-xl lg:sticky lg:top-0 lg:border-r lg:border-white/10 lg:shadow-2xl">
    {/* Brand skeleton */}
    <div className="flex items-center justify-between px-5 py-5">
      <div className="flex items-center gap-2.5">
        <div className="h-10 w-10 rounded-xl bg-emerald-600/40 animate-pulse" />
        <div className="space-y-1.5">
          <div className="h-4 w-32 bg-emerald-600/40 rounded animate-pulse" />
          <div className="h-3 w-20 bg-emerald-600/30 rounded animate-pulse" />
        </div>
      </div>
    </div>

    {/* Navigation skeleton */}
    <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-lg px-3 py-2.5"
        >
          <div className="h-5 w-5 bg-emerald-600/40 rounded animate-pulse" />
          <div className="h-4 w-24 bg-emerald-600/40 rounded animate-pulse" />
        </div>
      ))}
    </nav>

    {/* Footer skeleton */}
    <div className="space-y-3 border-t border-white/10 px-3 py-4">
      <div className="flex w-full items-center gap-3 rounded-lg px-3 py-2">
        <div className="h-5 w-5 bg-red-600/30 rounded animate-pulse" />
        <div className="h-4 w-20 bg-red-600/30 rounded animate-pulse" />
      </div>

      <div className="flex items-center gap-2.5 rounded-lg bg-white/5 px-3 py-2.5 border border-white/5">
        <div className="h-8 w-8 rounded-full bg-emerald-600/40 animate-pulse" />
        <div className="space-y-1.5 flex-1">
          <div className="h-4 w-24 bg-emerald-600/40 rounded animate-pulse" />
          <div className="h-3 w-16 bg-emerald-600/30 rounded animate-pulse" />
        </div>
      </div>
    </div>
  </aside>
);

export default function Sidebar({ open, onClose }) {
  const { logout, partner, loading } = useAuth();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // Show skeleton if loading or partner data is not available
  const isPartnerDataReady = partner?.kind !== undefined;
  
  // Only determine nav items if data is ready
  const navItems = isPartnerDataReady 
    ? partner?.kind === "CHAIN" ? CHAIN_NAV : BRANCH_NAV
    : [];

  // If still loading or partner data not available, show skeleton
  if (loading || !isPartnerDataReady) {
    return (
      <>
        {open && (
          <div
            className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={onClose}
          />
        )}
        <div
          className={`fixed inset-y-0 left-0 z-40 transition-transform duration-300 lg:sticky lg:top-0 lg:translate-x-0 ${
            open ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <SidebarSkeleton />
        </div>
      </>
    );
  }

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setIsLoggingOut(false);
      setLogoutModalOpen(false);
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar: full-size and persistent on desktop, slide-in drawer on mobile */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex h-screen w-64 shrink-0 flex-col bg-gradient-to-b from-emerald-800 via-emerald-900 to-green-900 shadow-xl transition-transform duration-300 lg:sticky lg:top-0 lg:translate-x-0 lg:border-r lg:border-white/10 lg:shadow-2xl ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Brand */}
        <div className="flex items-center justify-between px-5 py-5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-400 to-green-500 text-white shadow-lg shadow-emerald-500/30">
              <img
                src="/logo.jpg"
                alt="Alfennzo Logo"
                className="h-full w-full object-contain rounded-lg"
              />
            </div>
            <div className="leading-tight">
              <p className="text-label-md font-semibold tracking-tight text-white">
                Alfennzo Management
              </p>
              <p className="text-[11px] font-medium text-emerald-200/70">
                Partner Portal
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-white/60 transition hover:bg-white/10 hover:text-white lg:hidden"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav — icon + label, always visible */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-body-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-emerald-500/20 text-white shadow-lg shadow-emerald-500/20 border border-emerald-400/30"
                    : "text-emerald-100/70 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <Icon size={18} strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="space-y-3 border-t border-white/10 px-3 py-4">
          <button
            onClick={() => setLogoutModalOpen(true)}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-body-sm font-medium text-red-300 transition hover:bg-red-500/10 hover:text-red-200"
          >
            <LogOut size={17} />
            Log out
          </button>

          <NavLink
            to="/profile"
            onClick={onClose}
            className="flex items-center gap-2.5 rounded-lg bg-white/5 px-3 py-2.5 transition hover:bg-white/10 border border-white/5"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-green-500 text-label-sm font-semibold text-white shadow-md shadow-emerald-500/20 ring-2 ring-emerald-400/30">
              {partner?.name?.[0] ?? "P"}
            </div>
            <div className="min-w-0 leading-tight">
              <p className="truncate text-body-sm font-medium text-white">
                {partner?.name ?? "Partner"}
              </p>
              <p className="truncate text-[11px] text-emerald-200/60">
                {partner?.partnerId ?? ""}
              </p>
            </div>
          </NavLink>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      <Modal
        open={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        title="Sign Out"
        description="Are you sure you want to sign out of your account?"
        maxWidth="max-w-md"
        className="bg-white/95 backdrop-blur-sm border border-emerald-100 shadow-2xl shadow-emerald-200/50"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-red-50/50 rounded-xl border border-red-200/50">
            <div className="p-3 bg-red-100 rounded-full">
              <Power className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                You'll be signed out
              </p>
              <p className="text-xs text-gray-500">
                You can sign back in anytime using your credentials.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-amber-50/50 rounded-lg border border-amber-200/50">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <p className="text-xs text-amber-700">
              Any unsaved changes will be lost. Make sure to save your work
              before signing out.
            </p>
          </div>

          <div className="flex justify-end gap-2.5 pt-2">
            <button
              onClick={() => setLogoutModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg shadow-red-500/25 hover:shadow-red-500/40 hover:scale-[1.02] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoggingOut ? "Signing out..." : "Yes, Sign Out"}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}